<script>
export default {
  name: 'ValidatedInput',
  props: {
    value: {
      type: String,
      required: true,
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    isValid: {
      type: Boolean,
      required: false,
      default: true,
    },
    errorMessage: {
      type: String,
    },
    // New props for regex validation
    regex: {
      type: [RegExp, String],
      required: false,
    },
    customErrorMessage: {
      type: String,
      required: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPassword: false,
      internalIsValid: true,
      internalErrorMessage: '',
    };
  },
  computed: {
    computedIsValid() {
      // If isValid prop is provided, use it (for external validation)
      if (this.isValid !== true) {
        return this.isValid;
      }
      // Otherwise use internal validation
      return this.internalIsValid;
    },
    computedErrorMessage() {
      // If errorMessage prop is provided, use it (for external validation)
      if (this.errorMessage) {
        return this.errorMessage;
      }
      // Otherwise use internal validation message
      return this.internalErrorMessage;
    },
  },
  watch: {
    value: {
      handler(newValue) {
        this.validateInput(newValue);
      },
      immediate: true,
    },
    regex: {
      handler() {
        this.validateInput(this.value);
      },
      immediate: true,
    },
  },
  methods: {
    onNativeInput(event) {
      const newValue = event.target.value;
      this.$emit('input', newValue);
      this.validateInput(newValue);
    },
    onNativeChange(event) {
      const newValue = event.target.value;
      this.validateInput(newValue);
      this.$emit('change', newValue);
    },
    
    validateInput(value) {
      // If no regex is provided, consider it valid
      if (!this.regex) {
        this.internalIsValid = true;
        this.internalErrorMessage = '';
        return;
      }
      
      // Handle required field validation
      if (this.required && (!value || value.trim() === '')) {
        this.internalIsValid = false;
        this.internalErrorMessage = this.customErrorMessage || `${this.label || 'This field'} is required`;
        return;
      }
      
      // If field is empty and not required, consider it valid
      if (!value || value.trim() === '') {
        this.internalIsValid = true;
        this.internalErrorMessage = '';
        return;
      }
      
      // Convert string regex to RegExp if needed
      let regexPattern;
      if (typeof this.regex === 'string') {
        try {
          regexPattern = new RegExp(this.regex);
        } catch (e) {
          console.error('Invalid regex pattern:', this.regex);
          this.internalIsValid = false;
          this.internalErrorMessage = 'Invalid validation pattern';
          return;
        }
      } else {
        regexPattern = this.regex;
      }
      
      // Perform regex validation
      const isValid = regexPattern.test(value);
      this.internalIsValid = isValid;
      
      if (!isValid) {
        this.internalErrorMessage = this.customErrorMessage || `${this.label || 'This field'} format is invalid`;
      } else {
        this.internalErrorMessage = '';
      }
    },
  },
};
</script>

<template lang="pug">
.form-group
  label(v-if="label") {{ label }}
  input.form-control(
    :value="value",
    :placeholder="placeholder",
    :class="{ 'invalid-input': !computedIsValid }"
    @input="onNativeInput",
    @change="onNativeChange",
    :readonly="readonly"
  )
  p.error-message(v-if="!computedIsValid && computedErrorMessage") {{ computedErrorMessage }}
</template>

<style lang="scss">
.invalid-input {
  box-shadow: 0 0 0 1px #cc0404ba;
}
.error-message {
  color: #cc0404ba;
}
</style>